import BookmarkPlusDefaultIcon from "@/assets/images/icons/bookmark-plus-default.svg";
import BookmarkPlusHoverIcon from "@/assets/images/icons/bookmark-plus-hover.svg";
import BookmarkPlusPressedIcon from "@/assets/images/icons/bookmark-plus-pressed.svg";
import { BookmarkSave } from "@/components/BookmarkSave";
import { CoursePageShell } from "@/components/course/CoursePageShell";
import { CourseSearchBar } from "@/components/course/CourseSearchBar";
import { LessonPreview } from "@/components/course/LessonPreview";
import { LessonsGridList } from "@/components/lesson/LessonsGridList";
import { DashboardViewModeToggle } from "@/components/ui/DashboardViewModeToggle";
import { useErrorHandler } from "@/components/ui/ErrorHandlerProvider";
import { useDefaultLessonViewMode } from "@/hooks/useDefaultLessonViewMode";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useSearchTracking } from "@/hooks/useSearchTracking";
import { useSimpleCourseData } from "@/hooks/useSimpleCourseData";
import { useAuth } from "@/lib/AuthContext";
import { CourseStatus } from "@/lib/constants";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT,
  dashboardToolbarArtworkButton,
  DASHBOARD_TOOLBAR_ARTWORK_PX,
} from "@/lib/dashboardToolbarUi";
import { useIsMobile, useLessonCardLayout } from "@/lib/useDeviceType";
import { lessonSearchPlaceholder } from "@/lib/courseSearchPlaceholder";
import { isCourseBookmarked } from "@/supabase/services";
import type { Course, Lesson } from "@/types";
import { buildLessonUrl } from "@/utils/buildLessonUrl";
import { shouldSkipPreview as shouldSkipPreviewUtil } from "@/utils/lessonHelpers";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import { Button, XStack } from "tamagui";

type SimpleCourseViewProps = {
  course: Course;
  courseSlug: string;
  courseId?: string;
  minStatus: number;
};

export function SimpleCourseView({
  course,
  courseSlug,
  courseId,
  minStatus,
}: SimpleCourseViewProps) {
  const { user } = useAuth();
  const { showError, showSuccess } = useErrorHandler();
  const router = useRouter();
  const isMobile = useIsMobile();
  const lessonCardLayout = useLessonCardLayout();
  const isAnon = !!user?.is_anonymous;

  const courseAvatarUrl = (
    isMobile
      ? course.avatar_url_mobile?.trim()
      : course.avatar_url_desktop?.trim()
  ) || null;

  const { lessons, userLessonsMap, isLoading, error, queries } =
    useSimpleCourseData(course.id, user?.id, minStatus);

  const viewModeKey = `course_view_mode_${courseSlug}`;
  const [viewMode, setViewMode, viewModeLoaded, viewModeHadStored] =
    usePersistedState<"grid" | "list">(viewModeKey, "list");

  useDefaultLessonViewMode(
    viewModeLoaded,
    viewModeHadStored,
    lessons.length,
    setViewMode,
  );

  const [refreshing, setRefreshing] = useState(false);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCourseBookmarkedState, setIsCourseBookmarkedState] = useState(false);
  const [isBookmarkHovered, setIsBookmarkHovered] = useState(false);

  const courseIdNum = course.id != null ? Number(course.id) : undefined;

  useEffect(() => {
    if (isAnon || !user?.id || courseIdNum == null) return;
    isCourseBookmarked(user.id, courseIdNum)
      .then(setIsCourseBookmarkedState)
      .catch(() => {});
  }, [isAnon, user?.id, courseIdNum]);

  const refetchAll = useCallback(async () => {
    await Promise.all([
      queries.lessonsQuery.refetch(),
      queries.userLessonsQuery.refetch(),
    ]);
  }, [queries]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchAll();
    } finally {
      setRefreshing(false);
    }
  }, [refetchAll]);

  const shouldSkipPreview = useCallback(
    (l: Lesson) =>
      shouldSkipPreviewUtil(l, userLessonsMap, {
        viewMode,
        includeSummaryVisible: true,
      }),
    [userLessonsMap, viewMode],
  );

  const filteredLessons = useMemo(() => {
    if (course.metadata?.allow_lesson_search !== true) return lessons;
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return lessons;
    return lessons.filter(
      (l) =>
        (l.title || "").toLowerCase().includes(q) ||
        (l.summary || "").toLowerCase().includes(q),
    );
  }, [lessons, searchQuery, course.metadata?.allow_lesson_search]);

  const searchTracking = useSearchTracking({
    scope: "in_course",
    courseId: courseIdNum,
    courseSlug: String(courseSlug),
    courseTitle: course.title,
  });

  const isSearchActive =
    course.metadata?.allow_lesson_search === true &&
    searchQuery.trim().length >= 2;

  useEffect(() => {
    if (!isSearchActive) return;
    searchTracking.onResultsSettled(
      searchQuery.trim(),
      filteredLessons.map((l) => ({
        id: l.id,
        type: "lesson" as const,
        slug: l.slug,
        title: l.title,
        courseSlug: String(courseSlug),
      })),
    );
  }, [isSearchActive, searchQuery, filteredLessons, searchTracking, courseSlug]);

  const handleLessonPress = useCallback(
    (l: Lesson) => {
      if (isSearchActive) {
        const position = filteredLessons.findIndex((r) => r.id === l.id) + 1;
        searchTracking.trackResultClick(
          searchQuery.trim(),
          {
            id: l.id,
            type: "lesson",
            slug: l.slug,
            title: l.title,
            courseSlug: String(courseSlug),
          },
          position,
        );
      }
      if (shouldSkipPreview(l)) {
        router.push(
          buildLessonUrl({
            courseSlug: String(courseSlug),
            lessonSlug: l.slug,
          }) as any,
        );
        return;
      }
      setLesson(l);
      if (isMobile) {
        setModalOpen(false);
        setSheetOpen(true);
      } else {
        setSheetOpen(false);
        setModalOpen(true);
      }
    },
    [
      shouldSkipPreview,
      router,
      courseSlug,
      isMobile,
      isSearchActive,
      filteredLessons,
      searchQuery,
      searchTracking,
    ],
  );


  const courseUrl = `/courses/${courseSlug}`;

  const courseBookmarkButton =
    !isAnon && user && courseIdNum != null ? (
      <BookmarkSave
        mode="course"
        onSave={setIsCourseBookmarkedState}
        showError={showError}
        showSuccess={showSuccess}
        courseId={courseIdNum}
        courseUrl={courseUrl}
        placement="bottom-end"
      >
        <Button
          {...dashboardToolbarArtworkButton}
          onMouseEnter={Platform.OS === "web" ? () => setIsBookmarkHovered(true) : undefined}
          onMouseLeave={Platform.OS === "web" ? () => setIsBookmarkHovered(false) : undefined}
        >
          {isCourseBookmarkedState ? (
            <BookmarkPlusPressedIcon
              width={DASHBOARD_TOOLBAR_ARTWORK_PX}
              height={DASHBOARD_TOOLBAR_ARTWORK_PX}
            />
          ) : isBookmarkHovered ? (
            <BookmarkPlusHoverIcon
              width={DASHBOARD_TOOLBAR_ARTWORK_PX}
              height={DASHBOARD_TOOLBAR_ARTWORK_PX}
            />
          ) : (
            <BookmarkPlusDefaultIcon
              width={DASHBOARD_TOOLBAR_ARTWORK_PX}
              height={DASHBOARD_TOOLBAR_ARTWORK_PX}
            />
          )}
        </Button>
      </BookmarkSave>
    ) : null;

  const viewToggle = (
    <XStack
      ai="center"
      gap="$1.5"
      h={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
      minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
      $gtSm={{ gap: "$2" }}
    >
      {courseBookmarkButton}
      <DashboardViewModeToggle
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    </XStack>
  );

  return (
    <>
      <CoursePageShell
        courseId={course.id ? String(course.id) : courseId}
        courseTitle={course.title}
        courseSummary={course.summary ?? null}
        courseAvatarUrl={courseAvatarUrl}
        courseSkillLevel={course.skill_level ?? null}
        isLoading={isLoading || !viewModeLoaded}
        error={error}
        onRetry={refetchAll}
        refreshing={refreshing}
        onRefresh={onRefresh}
        courseStatus={course.status}
        canViewDrafts={minStatus < CourseStatus.Published}
        renderScroller={({ banner, footer }) => (
          <LessonsGridList
            lessons={filteredLessons}
            viewMode={viewMode}
            isMobile={isMobile}
            searchQuery={
              course.metadata?.allow_lesson_search ? searchQuery : ""
            }
            userLessonsMap={userLessonsMap}
            onPressLesson={handleLessonPress}
            lessonCardLayout={lessonCardLayout}
            hidePublishedDate={!course.metadata?.show_lesson_dates}
            refreshing={refreshing}
            onRefresh={onRefresh}
            ListHeaderComponent={
              <>
                {banner}
                <CourseSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onClear={() => setSearchQuery("")}
                  courseSlug={courseSlug}
                  showLessonSearch={
                    course.metadata?.allow_lesson_search === true
                  }
                  searchPlaceholder={lessonSearchPlaceholder(course.metadata)}
                  discussionUrl={course.discussion_url ?? null}
                  rightContent={viewToggle}
                />
              </>
            }
            ListFooterComponent={footer}
          />
        )}
      />

      <LessonPreview
        lesson={lesson}
        sheetOpen={sheetOpen}
        modalOpen={modalOpen}
        onSheetChange={setSheetOpen}
        onModalChange={setModalOpen}
        onPlayPress={() => {
          setModalOpen(false);
          setSheetOpen(false);
        }}
        userLessonsMap={userLessonsMap}
        courseSlug={courseSlug}
        userId={isAnon ? undefined : user?.id}
        hidePublishedDate={!course.metadata?.show_lesson_dates}
        showError={showError}
        showSuccess={showSuccess}
        isAnon={isAnon}
      />
    </>
  );
}
